"use client";

import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectItem } from "@/components/ui/select";
import { FacultySelect } from "@/components/faculty-select";
import { Switch } from "@/components/ui/switch";
import type { PermissionDefinition } from "@/lib/permissions";
import { UserAPIService } from "@/lib/user-api.service";
import { Toast, getSafeErrorMessage } from "@/lib/utils";
import { Loader2 } from "lucide-react";
import { ROLE_LABELS, ROLE_OPTIONS } from "@/lib/roles";
import { ModalShell } from "@/components/ui/modal-shell";

interface User {
	id: string;
	name: string;
	email: string;
	role?: string;
	banned?: boolean;
	banReason?: string;
	banExpires?: Date;
	createdAt: Date;
	updatedAt: Date;
	MaCB?: string;
	faculty_code?: string | null;
	/** `{"export_excel": true}` — khoá vắng mặt nghĩa là không có quyền. */
	permissions?: Record<string, boolean> | null;
}

interface EditUserModalProps {
	isOpen: boolean;
	user: User;
	onClose: () => void;
}

export function EditUserModal({ isOpen, user, onClose }: EditUserModalProps) {
	const [formData, setFormData] = useState({
		name: user.name,
		email: user.email,
		role: (user.role || "user") as "user" | "admin" | "manager",
		MaCB: user.MaCB || "",
		faculty_code: user.faculty_code || "",
	});
	// Quyền để riêng khỏi `formData`: danh sách khoá do server quyết định, không
	// phải một tập trường cố định của form này.
	const [permissions, setPermissions] = useState<Record<string, boolean>>({});
	const [catalog, setCatalog] = useState<PermissionDefinition[]>([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState("");

	// Danh mục tải một lần khi mở modal. Nhãn đến từ server để thêm một quyền
	// mới chỉ phải sửa một chỗ (danh mục ở backend), không phải sửa cả UI.
	useEffect(() => {
		if (!isOpen) return;
		let cancelled = false;
		UserAPIService.getPermissionCatalog()
			.then((data) => {
				if (!cancelled) setCatalog(data);
			})
			.catch((err) => console.error("Failed to load permission catalog:", err));
		return () => {
			cancelled = true;
		};
	}, [isOpen]);

	useEffect(() => {
		if (isOpen) {
			setFormData({
				name: user.name,
				email: user.email,
				role: (user.role || "user") as "user" | "admin" | "manager",
				MaCB: user.MaCB || "",
				faculty_code: user.faculty_code || "",
			});
			setPermissions({ ...(user.permissions ?? {}) });
			setError("");
		}
	}, [isOpen, user]);

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setError("");
		setLoading(true);

		try {
			// Tên và mã CB đi qua endpoint admin của chính dự án, không qua
			// `authClient.updateUser`: endpoint đó luôn ghi vào tài khoản đang đăng
			// nhập, nên admin sửa người khác sẽ đổi nhầm tên của chính mình. Mã CB
			// cũng chưa từng được gửi đi trong đường cũ.
			if (formData.name !== user.name || formData.MaCB !== (user.MaCB || "")) {
				try {
					await UserAPIService.updateProfile(user.id, {
						name: formData.name,
						MaCB: formData.MaCB,
					});
				} catch (profileError) {
					console.error("Failed to update profile:", profileError);
					setError(
						profileError instanceof Error
							? profileError.message
							: "Không thể cập nhật thông tin người dùng",
					);
					return;
				}
			}

			// Cùng lý do như danh sách quản lý dùng endpoint này: `admin.setRole` của
			// Better Auth không biết vai trò `manager` của dự án.
			if (formData.role !== (user.role || "user")) {
				try {
					await UserAPIService.updateUserRole(user.id, formData.role);
				} catch (roleError) {
					console.error("Failed to update role:", roleError);
					setError(
						roleError instanceof Error ? roleError.message : "Không thể cập nhật quyền người dùng",
					);
					return;
				}
			}

			// Chỉ gọi khi giá trị thực sự đổi: gán lại đúng mã đang có vẫn là một lần
			// ghi quyền, và sẽ thất bại một cách khó hiểu nếu khoa đó vừa bị ngừng
			// hoạt động — trong khi người dùng không đổi gì cả.
			const currentFacultyCode = user.faculty_code || "";
			if (formData.faculty_code !== currentFacultyCode) {
				try {
					await UserAPIService.updateFacultyCode(user.id, formData.faculty_code || null);
				} catch (facultyError) {
					// `AuthenticatedAPIClient` đã toast lý do; ở đây giữ modal mở để sửa
					// tiếp thay vì đóng lại như thể đã lưu xong.
					console.error("Failed to update faculty code:", facultyError);
					setError(
						facultyError instanceof Error
							? facultyError.message
							: "Không thể cập nhật khoa cho người dùng",
					);
					return;
				}
			}

			// Cùng lý do như khoa: chỉ gọi cho quyền THỰC SỰ đổi, vì mỗi lần gọi là
			// một lần ghi quyền chứ không phải một trường hồ sơ được lưu kèm.
			const before = user.permissions ?? {};
			const changed = catalog.filter(
				(p) => (permissions[p.key] === true) !== (before[p.key] === true),
			);

			for (const permission of changed) {
				try {
					await UserAPIService.updatePermission(
						user.id,
						permission.key,
						permissions[permission.key] === true,
					);
				} catch (permissionError) {
					console.error("Failed to update permission:", permission.key, permissionError);
					setError(
						permissionError instanceof Error
							? permissionError.message
							: `Không thể cập nhật quyền "${permission.label}"`,
					);
					return;
				}
			}

			Toast.success(`Đã cập nhật ${formData.name} thành công!`);
			onClose();
		} catch (error: any) {
			console.error("Failed to update user:", error);
			setError(getSafeErrorMessage(error));
		} finally {
			setLoading(false);
		}
	};

	const handleInputChange = (field: string, value: string) => {
		setFormData((prev) => ({
			...prev,
			[field]: value,
		}));
	};

	return (
		/* `max-w-3xl` cho hai cột đủ rộng mà không tràn chữ. Phần cuộn / chiều cao
		   tối đa do ModalShell lo. */
		<ModalShell
			isOpen={isOpen}
			onClose={onClose}
			title="Chỉnh sửa người dùng"
			size="3xl"
			onSubmit={handleSubmit}
			footer={
				<div className="flex gap-3">
					<Button
						type="button"
						variant="outline"
						onClick={onClose}
						disabled={loading}
						className="flex-1"
					>
						Hủy
					</Button>
					<Button type="submit" disabled={loading} className="flex-1">
						{loading ? (
							<>
								<Loader2 className="mr-2 h-4 w-4 animate-spin" />
								Đang cập nhật...
							</>
						) : (
							"Cập nhật"
						)}
					</Button>
				</div>
			}
		>
			<div className="space-y-4">
				{error && (
					<div className="bg-red-50 border border-red-200 rounded-md p-3 dark:bg-red-950 dark:border-red-800">
						<p className="text-red-800 dark:text-red-300 text-sm">{error}</p>
					</div>
				)}

				{/* Hai cột cho các field ngắn — §4 CLAUDE.md. Gộp một cột dọc dài là
						    thứ đã đẩy hàng nút ra khỏi màn hình. */}
				<div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
					<div>
						<label className="block text-sm font-medium text-muted-foreground mb-1">
							Họ tên <span className="text-red-500 dark:text-red-400">*</span>
						</label>
						<Input
							type="text"
							value={formData.name}
							onChange={(e) => handleInputChange("name", e.target.value)}
							placeholder="Nhập họ tên"
							required
						/>
					</div>

					<div>
						<label className="block text-sm font-medium text-muted-foreground mb-1">Email</label>
						<Input type="email" value={formData.email} disabled className="bg-muted" />
						<p className="text-xs text-muted-foreground mt-1">Email không thể thay đổi</p>
					</div>

					<div>
						<label className="block text-sm font-medium text-muted-foreground mb-1">Mã CB</label>
						<Input
							type="text"
							value={formData.MaCB}
							onChange={(e) => handleInputChange("MaCB", e.target.value)}
							placeholder="Nhập mã CB (tùy chọn)"
						/>
					</div>

					<div>
						<label className="block text-sm font-medium text-muted-foreground mb-1">Quyền</label>
						<Select
							value={formData.role}
							onChange={(e) => handleInputChange("role", e.target.value)}
						>
							{ROLE_OPTIONS.map((value) => (
								<SelectItem key={value} value={value}>
									{ROLE_LABELS[value]}
								</SelectItem>
							))}
						</Select>
					</div>

					{/* Khoa chiếm cả hàng: tên khoa dài theo bản chất, nhồi vào nửa cột sẽ
							    bị cắt hoặc xuống dòng xấu. */}
					<div className="sm:col-span-2">
						<label
							htmlFor="edit-user-faculty"
							className="block text-sm font-medium text-muted-foreground mb-1"
						>
							Khoa
						</label>
						<FacultySelect
							id="edit-user-faculty"
							value={formData.faculty_code}
							onChange={(code) => handleInputChange("faculty_code", code)}
							disabled={loading}
						/>
					</div>
				</div>

				{/* Danh sách quyền dựng từ danh mục của server: thêm một quyền ở backend
				    là nó tự xuất hiện ở đây, không phải sửa thêm chỗ nào. */}
				{catalog.length > 0 && (
					<div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
						{catalog.map((permission) => (
							<div
								key={permission.key}
								className="flex items-start justify-between gap-4 rounded-md border p-3"
							>
								<div>
									<label
										htmlFor={`edit-user-permission-${permission.key}`}
										className="block text-sm font-medium text-muted-foreground"
									>
										{permission.label}
									</label>
									<p className="text-xs text-muted-foreground">{permission.description}</p>
								</div>
								<Switch
									id={`edit-user-permission-${permission.key}`}
									checked={permissions[permission.key] === true}
									onCheckedChange={(checked) =>
										setPermissions((prev) => ({ ...prev, [permission.key]: checked }))
									}
									disabled={loading}
								/>
							</div>
						))}
					</div>
				)}
			</div>
		</ModalShell>
	);
}
